import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronRight, Copy, Link2, MessageCircle, MoreHorizontal, Plus, QrCode, Sparkles, Users } from "lucide-react";
import ReferralQr from "@/components/ReferralQr";
import { RewardRulesDialog } from "@/components/RewardRulesDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/components/ui/sonner";
import { MEMBER_RULES, REFERRAL } from "@/constants/testIds";
import { createReferralShare, currency, displayTier, roleLabel } from "@/lib/api";

export const StatusPill = ({ status }) => {
  const labels = { new_lead: "New lead", survey: "Survey", proposal: "Proposal", won: "Won", commissioned: "Commissioned", closed: "Closed", pending: "Pending", held: "Held", approved: "Approved", paid: "Paid", clawback: "Clawback" };
  return <span className={`status-pill status-${status}`} data-testid={`status-${status}`}>{labels[status] || status}</span>;
};

export const EmptyState = ({ title, body, action }) => <div className="empty-state"><div className="empty-icon"><Sparkles size={20} /></div><h3>{title}</h3><p>{body}</p>{action}</div>;

const StatCard = ({ label, value, featured, note }) => <div className={`premium-card stat-card ${featured ? "stat-featured" : ""}`}><p>{label}</p><strong className="tabular">{value}</strong>{note && <span>{note}</span>}</div>;

const tierProgressLabel = (tier, installed, next) => {
  if (tier === "platinum") return "Highest badge reached";
  return `${installed} of ${next} commissioned referrals`;
};

export const TierCard = ({ member, stats }) => {
  const tier = member?.customer_tier || "silver";
  const installed = stats?.successful_installs || 0;
  const thresholds = { silver: 3, gold: 6, platinum: 6 };
  const next = thresholds[tier];
  const progress = tier === "platinum" ? 100 : Math.min(100, (installed / next) * 100);
  return <section data-testid="loyalty-tier-card"><RewardRulesDialog><button type="button" className={`tier-card tier-${tier} w-full text-left`} data-testid={MEMBER_RULES.trigger}><div className="tier-metal"><span className="tier-mark"><Sparkles size={17} /></span><div><p>MEMBER BADGE</p><h3>{tier[0].toUpperCase() + tier.slice(1)} Member</h3></div></div><div className="tier-body"><div className="tier-progress-copy"><span>{tierProgressLabel(tier, installed, next)}</span><b>{Math.round(progress)}%</b></div><Progress value={progress} className="h-2.5 bg-black/10 [&>div]:bg-brand-gold" /><p>Badges recognise your commissioned referrals. Your reward per referral is set by the reward rules, not by your badge.</p><span className="mt-3 flex items-center justify-between text-xs font-semibold text-brand-gold-deep">How rewards work <ChevronRight size={16} /></span></div></button></RewardRulesDialog></section>;
};

const escapeForRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The server-rendered share text carries the personal link, which may already
// end in a dated `?s=` query. Swap the whole url, query string included, so the
// message never ends up with two share ids stacked on one link.
const withDatedLink = (text, plainLink, datedLink) => {
  if (!text || !plainLink || !datedLink || datedLink === plainLink) return text;
  const linkPattern = new RegExp(`${escapeForRegExp(plainLink)}(\\?[^\\s]*)?`);
  return linkPattern.test(text) ? text.replace(linkPattern, () => datedLink) : text;
};

// A share that could not be created still has to be shareable, so fall back to
// the plain personal link. The QR keeps its legacy source marker in that case.
const fallbackShareUrl = (channel, referralLink) =>
  channel === "qr" && referralLink ? `${referralLink}?s=qr` : referralLink;

export const ReferralHero = ({ member }) => {
  const code = member?.referral_code || "–";
  const referralLink = member?.referral_link || "";
  const shareText = member?.share_text || "";
  const [shareUrls, setShareUrls] = useState({});
  const shareCache = useRef({});

  // Each channel asks the backend for its own dated link (`?s=<share id>`) and
  // keeps it for the life of the card, so repeat copies reuse a single share.
  const shareUrlFor = useCallback(
    async (channel) => {
      if (!referralLink) return referralLink;
      if (shareCache.current[channel]) return shareCache.current[channel];
      try {
        const share = await createReferralShare(channel);
        const url = share?.url || fallbackShareUrl(channel, referralLink);
        shareCache.current[channel] = url;
        setShareUrls((current) => ({ ...current, [channel]: url }));
        return url;
      } catch {
        toast.error("Could not refresh your share link. Using your standard link.");
        return fallbackShareUrl(channel, referralLink);
      }
    },
    [referralLink],
  );

  useEffect(() => {
    shareCache.current = {};
    setShareUrls({});
    if (referralLink) shareUrlFor("link");
  }, [referralLink, shareUrlFor]);

  const displayLink = shareUrls.link || referralLink;
  const displayShareText = withDatedLink(shareText, referralLink, displayLink);
  const qrLink = shareUrls.qr || fallbackShareUrl("qr", referralLink);

  const copyValue = async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch {
      toast.error("Could not copy to the clipboard");
    }
  };
  const copyCode = () => copyValue(code, "Referral code copied");
  const copyLink = async () => copyValue(await shareUrlFor("link"), "Referral link copied");
  const whatsapp = async () => {
    const url = await shareUrlFor("whatsapp");
    window.open(`https://wa.me/?text=${encodeURIComponent(withDatedLink(shareText, referralLink, url))}`, "_blank", "noopener,noreferrer");
  };
  const more = async () => {
    const url = await shareUrlFor("other");
    const text = withDatedLink(shareText, referralLink, url);
    try {
      if (navigator.share) await navigator.share({ text, url });
      else await copyValue(text, "Share message copied");
    } catch {
      toast.error("Could not open sharing options");
    }
  };
  return <section className="referral-hero" data-testid={REFERRAL.hero}><div className="hero-orbit" /><div className="hero-copy"><p><span className="pulse-dot" /> YOUR REFERRAL CODE</p><h2 data-testid="referral-code-value">{code}</h2><button type="button" className="hero-code-copy" data-testid="referral-code-copy-button" onClick={copyCode}><Copy />Copy code</button>{referralLink ? <a href={displayLink} data-testid={REFERRAL.link}>{displayLink}</a> : <span className="hero-link-unavailable" data-testid={REFERRAL.link}>Personal link unavailable</span>}<span className="hero-share-copy">{displayShareText}</span><div className="hero-actions"><Button data-testid={REFERRAL.copyLinkButton} onClick={copyLink} variant="secondary" disabled={!referralLink}><Link2 />Copy link</Button><Dialog><DialogTrigger asChild><Button data-testid={REFERRAL.qrButton} variant="secondary" disabled={!referralLink} onClick={() => shareUrlFor("qr")}><QrCode />QR</Button></DialogTrigger><DialogContent className="referral-qr-dialog" data-testid={REFERRAL.qrDialog}><DialogHeader><DialogTitle>Your referral QR</DialogTitle><DialogDescription>Ask someone to scan this code to open your personal link.</DialogDescription></DialogHeader>{referralLink ? <ReferralQr referralLink={qrLink} code={code} /> : null}</DialogContent></Dialog><Button data-testid={REFERRAL.whatsappButton} onClick={whatsapp} className="gold-button" disabled={!shareText}><MessageCircle />WhatsApp</Button><Button data-testid={REFERRAL.moreShareButton} aria-label="More share options" onClick={more} variant="ghost" className="text-white hover:bg-white/10 hover:text-white" disabled={!referralLink || !shareText}><MoreHorizontal /></Button></div></div></section>;
};

export const ProfileCard = ({ member }) => {
  const tier = displayTier(member);
  const rows = [
    ["Name", member?.name],
    ["Email", member?.email],
    ["Phone", member?.phone],
    ["Location", [member?.city, member?.state].filter(Boolean).join(", ")],
    ["Membership", roleLabel(member?.role)],
    ["Tier", tier ? tier[0].toUpperCase() + tier.slice(1) : null],
  ];
  return (
    <section className="premium-card p-6" data-testid="member-profile-card">
      <div className="section-head">
        <div>
          <p className="eyebrow">YOUR PROFILE</p>
          <h3>Account details</h3>
        </div>
        <span className="rounded-full bg-brand-gold-soft px-3 py-1 text-[13px] font-bold tracking-[.04em] text-brand-gold-deep">{member?.referral_code || "–"}</span>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div className="flex flex-col gap-0.5" key={label}>
            <span className="text-[11px] font-semibold uppercase tracking-[.12em] text-brand-subtle">{label}</span>
            <b className="break-words text-[15px] font-semibold text-brand-ink">{value || "–"}</b>
          </div>
        ))}
      </div>
    </section>
  );
};

export const MemberOverview = ({ member, stats, referrals, onAddReferral }) => <div className="space-y-6"><ReferralHero member={member} /><div className="stats-grid"><StatCard label="Total referrals" value={stats?.total_referrals || 0} note="Lifetime" /><StatCard label="Successful installs" value={stats?.successful_installs || 0} note="Commissioned" /><StatCard label="Total earnings" value={currency(stats?.total_earnings)} featured note="Gross rewards" /></div><ProfileCard member={member} /><div className="dashboard-grid"><TierCard member={member} stats={stats} /><section className="premium-card recent-card"><div className="section-head"><div><p className="eyebrow">LATEST ACTIVITY</p><h3>Recent referrals</h3></div><Button variant="ghost" onClick={onAddReferral} data-testid="recent-add-referral">Add <Plus /></Button></div>{referrals?.length ? <div className="referral-list">{referrals.slice(0,4).map((item) => <div className="referral-row" key={item.id}><div className="avatar-soft">{item.prospect_name[0]}</div><div className="referral-main"><b>{item.prospect_name}</b><span>{item.prospect_city || "City pending"}</span></div><StatusPill status={item.stage} /><ArrowRight size={16} className="row-arrow" /></div>)}</div> : <EmptyState title="Your first referral starts here" body="Add a friend and we’ll mirror every progress update from Greentek ERP." action={<Button data-testid="empty-add-referral" onClick={onAddReferral}><Plus />Add referral</Button>} />}</section></div><section className="campaign-banner"><div><p>LIMITED CAMPAIGN · JULY</p><h3>Double your impact this solar season</h3><span>Refer a commercial project and unlock priority processing.</span></div><Button variant="secondary" onClick={onAddReferral}>Refer now <ArrowRight /></Button></section></div>;

const SOURCE_LABELS = { link: "Link", qr: "QR", manual: "Manual" };

const SYNC_STATUS_COPY = {
  created: "CRM lead created",
  linked: "Linked to existing CRM lead",
  failed: "ERP sync failed",
  queued: "ERP sync queued",
  conflict: "Already referred by another member",
};

export const ReferralList = ({ referrals, onAddReferral }) => <section className="premium-card table-card"><div className="section-head"><div><p className="eyebrow">YOUR NETWORK</p><h2>All referrals</h2><p>Progress is synced from Greentek's CRM.</p></div><Button data-testid="add-referral-open-button" onClick={onAddReferral}><Plus />Add referral</Button></div>{referrals?.length ? <div className="responsive-table"><div className="table-header"><span>Friend</span><span>City</span><span>Stage</span><span>Reward</span></div>{referrals.map((item) => <div className="table-row" key={item.id}><span data-label="Friend"><b>{item.prospect_name}</b><small>{item.prospect_phone}</small><small className={`referral-source source-${item.source || "manual"}`} data-testid={REFERRAL.sourceBadge}>{SOURCE_LABELS[item.source] || "Manual"}</small>{item.remarks && <small className="referral-remarks">{item.remarks}</small>}</span><span data-label="City">{item.prospect_city || "–"}</span><span data-label="Stage"><StatusPill status={item.stage} />{item.frappe_lead_id && <small>CRM lead: {item.frappe_lead_id}</small>}<small>{SYNC_STATUS_COPY[item.erp_sync_status] || ""}</small></span><span data-label="Reward" className="tabular"><b>{item.reward_amount ? currency(item.reward_amount) : "–"}</b></span></div>)}</div> : <EmptyState title="No referrals yet" body="Your submitted referrals will appear here with live ERP stages." action={<Button onClick={onAddReferral}><Users />Refer a friend</Button>} />}</section>;

const SCHEDULE_LABELS = {
  customer_referral: "Customer Referral",
  ambassador: "Ambassador",
  trade_partner: "Trade Partner",
};

const BAND_LABELS = {
  residential: "Residential",
  commercial_0_25: "Commercial up to 25 kW",
  commercial_26_100: "Commercial 26 to 100 kW",
  commercial_101_250: "Commercial 101 to 250 kW",
  commercial_251_plus: "Commercial above 250 kW",
  commercial_over_250: "Commercial above 250 kW",
  ambassador_step1: "Ambassador step 1",
  ambassador_step2: "Ambassador step 2",
  ambassador_step3: "Ambassador step 3",
};

const HELD_COPY = {
  deal_desk: "Awaiting deal desk review",
  capacity_missing: "Awaiting project capacity",
  schedule_not_launched: "Partner programme opens later",
  attribution_conflict: "Awaiting attribution review",
};

const rewardLabel = (value, labels) => labels[value] || value?.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const summaryWithoutHeld = (value, rewards, statuses) => {
  const raw = Number(value || 0);
  const included = rewards.filter((item) => !statuses || statuses.includes(item.status));
  const held = included.filter((item) => item.status === "held").reduce((sum, item) => sum + Number(item.net_amount || 0), 0);
  const rowTotal = included.reduce((sum, item) => sum + Number(item.net_amount || 0), 0);
  return held > 0 && Math.abs(raw - rowTotal) < 0.01 ? raw - held : raw;
};

export const RewardsView = ({ summary, rewards = [] }) => {
  const total = summaryWithoutHeld(summary?.total, rewards);
  const available = summaryWithoutHeld(summary?.available, rewards, ["pending", "approved", "held"]);
  return <div className="space-y-5"><div className="stats-grid reward-stats"><StatCard label="Total earnings" value={currency(total)} /><StatCard label="Available" value={currency(available)} featured /><StatCard label="Paid" value={currency(summary?.paid)} /></div><section className="premium-card table-card" data-testid="rewards-history"><div className="section-head"><div><p className="eyebrow">LEDGER</p><h2>Reward history</h2><p>Rule snapshots preserve every historical calculation.</p></div></div>{rewards.length ? <div className="responsive-table"><div className="table-header rewards-cols"><span>Referral</span><span>Status</span><span>Gross</span><span>TDS</span><span>Net / UTR</span></div>{rewards.map((item) => <div className="table-row rewards-cols" key={item.id}><span data-label="Referral"><b>{item.referral_name}</b><small>{item.rule_version}</small><small>{rewardLabel(item.schedule, SCHEDULE_LABELS)}</small><small>{rewardLabel(item.band, BAND_LABELS)}</small></span><span data-label="Status"><StatusPill status={item.status} />{item.status === "held" && <small>{HELD_COPY[item.hold_reason]}</small>}</span><span data-label="Gross" className="tabular">{currency(item.gross_amount)}</span><span data-label="TDS" className="tabular">{currency(item.tds_amount)}</span><span data-label="Net / UTR"><b className="tabular">{currency(item.net_amount)}</b><small>{item.utr || "UTR pending"}</small></span></div>)}</div> : <EmptyState title="No rewards calculated" body="Rewards appear after an installation is commissioned in Frappe." />}</section></div>;
};
